import { z } from "zod";
import { BILLING_MODES, COMPANY_STATUSES, PRODUCTION_TYPES_OF_COMPANY } from "../enums.ts";
import { Address, DateOnly, Money } from "./common.ts";

export const CompanyInput = z.object({
  name: z.string().min(1).max(200),
  dba: z.string().max(200).nullable().optional(),
  otherName: z.string().max(200).nullable().optional(),
  parentCompanyId: z.string().uuid().nullable().optional(),
  billingMode: z.enum(BILLING_MODES).default("own"),
  defaultBillToCompanyId: z.string().uuid().nullable().optional(),
  paymentTermsDays: z.number().int().min(0).max(180).nullable().optional(),
  productionType: z.enum(PRODUCTION_TYPES_OF_COMPANY).default("production"),
  programKey: z.string().max(40).default("food"),
  rcPersonId: z.string().uuid().nullable().optional(),
  coRcPersonId: z.string().uuid().nullable().optional(),
  // kosher policy (D2) — the company-level defaults
  restrictionType: z.string().max(60).nullable().optional(),
  kosherGroup: z.string().max(60).nullable().optional(),
  kosherForPesach: z.boolean().default(false),
  sellChometz: z.boolean().default(false),
  jewishOwnership: z.enum(["yes", "no", "unknown"]).default("unknown"),
  yoshon: z.enum(["na", "yes", "no"]).default("na"),
  cholovYisroel: z.boolean().default(false),
  pasYisroel: z.boolean().default(false),
  mevushal: z.boolean().default(false),
  fish: z.boolean().default(false),
  kitniyos: z.boolean().default(false),
  certifications: z.array(z.string().max(60)).default([]),
  certificateWording: z.string().max(400).nullable().optional(),
  // fees & contract (D1) — the contract is informational only
  adminFee: Money.nullable().optional(),
  monthlyFee: Money.default(0),
  annualFee: Money.nullable().optional(),
  productionBilling: z.boolean().default(false),
  contractType: z.string().max(60).nullable().optional(),
  contractStart: DateOnly.nullable().optional(),
  contractExpiration: DateOnly.nullable().optional(),
  // narrative
  companyOverview: z.string().max(8000).nullable().optional(),
  kosherNotes: z.string().max(8000).nullable().optional(),
  billingNotes: z.string().max(8000).nullable().optional(),
  directions: z.string().max(4000).nullable().optional(),
  website: z.string().max(300).nullable().optional(),
  generalPhone: z.string().max(60).nullable().optional(),
  generalEmail: z.string().email().max(200).nullable().optional(),
  listingCategory: z.string().max(80).nullable().optional(),
  // addresses (structured 5-part + place id)
  corporateAddress: Address.nullable().optional(),
  ownerName: z.string().max(200).nullable().optional(),
  ownerHomeAddress: Address.nullable().optional(),
  // billing block (D2 → D9)
  billingSameAsBusiness: z.boolean().default(true),
  billingName: z.string().max(200).nullable().optional(),
  billingAddress: Address.nullable().optional(),
  billingEmail: z.string().email().max(200).nullable().optional(),
  billingPhone: z.string().max(60).nullable().optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CompanyInput = z.infer<typeof CompanyInput>;

export const CompanyUpdateInput = CompanyInput.partial().extend({
  /** updatedAt (ISO) the client loaded — used for the stale-edit warning. */
  expectedUpdatedAt: z.string().optional(),
});
export type CompanyUpdateInput = z.infer<typeof CompanyUpdateInput>;

export const CompanyStatusInput = z.object({
  status: z.enum(COMPANY_STATUSES),
  reason: z.string().min(1).max(2000),
});

/** Approve an application: the first company user is invited in the same act. */
export const CompanyApproveInput = z.object({
  email: z.string().email().max(200).optional(),
  name: z.string().min(1).max(200).optional(),
  reason: z.string().max(2000).optional(),
});
export type CompanyApproveInput = z.infer<typeof CompanyApproveInput>;

export const OnboardingStageInput = z.object({
  stage: z.string().min(1).max(60),
  reason: z.string().max(2000).nullable().optional(),
});

export const CompanyQuery = z.object({
  q: z.string().max(200).optional(),
  status: z.enum(COMPANY_STATUSES).optional(),
  rcPersonId: z.string().uuid().optional(),
  productionType: z.enum(PRODUCTION_TYPES_OF_COMPANY).optional(),
  yoshon: z.enum(["yes", "no", "na"]).optional(),
  mine: z
    .string()
    .optional()
    .transform((v) => v === "1" || v === "true")
    .describe("staff: only companies where I am RC or co-RC"),
  parentCompanyId: z.string().uuid().optional(),
  sort: z.enum(["name", "updated"]).default("name"),
});

export const ContactInput = z.object({
  companyId: z.string().uuid().nullable().optional(),
  facilityId: z.string().uuid().nullable().optional(),
  plantId: z.string().uuid().nullable().optional(),
  name: z.string().min(1).max(200),
  email: z.string().email().max(200).nullable().optional(),
  phone: z.string().max(60).nullable().optional(),
  position: z.string().max(120).nullable().optional(),
  comments: z.string().max(2000).nullable().optional(),
  isPrimary: z.boolean().default(false),
  isBilling: z.boolean().default(false),
  hidden: z.boolean().default(false),
});
export type ContactInput = z.infer<typeof ContactInput>;
export const ContactUpdateInput = ContactInput.partial();

/** Public application form — narrow on purpose: no fees, RC or status. */
export const CompanyApplicationInput = z.object({
  businessName: z.string().min(1).max(200),
  dba: z.string().max(200).nullable().optional(),
  productionType: z.enum(PRODUCTION_TYPES_OF_COMPANY),
  businessAddress: Address,
  website: z.string().max(300).nullable().optional(),
  generalPhone: z.string().max(60).nullable().optional(),
  generalEmail: z.string().email().max(200),
  ownerName: z.string().min(1).max(200),
  ownerHomeAddress: Address.nullable().optional(),
  ownerPhone: z.string().max(60).nullable().optional(),
  ownerEmail: z.string().email().max(200).nullable().optional(),
  hasPartner: z.boolean().default(false),
  partnerName: z.string().max(200).nullable().optional(),
  partnerPhone: z.string().max(60).nullable().optional(),
  contactIs: z.enum(["owner", "partner", "other"]).default("owner"),
  contactName: z.string().max(200).nullable().optional(),
  contactPhone: z.string().max(60).nullable().optional(),
  contactEmail: z.string().email().max(200).nullable().optional(),
  productsDescription: z.string().max(4000).nullable().optional(),
  currentlyCertified: z.boolean().default(false),
  currentAgency: z.string().max(120).nullable().optional(),
  howHeard: z.string().max(200).nullable().optional(),
  /** honeypot — a real browser leaves it empty; anything else is silently discarded */
  fax: z.string().max(200).optional(),
});
export type CompanyApplicationInput = z.infer<typeof CompanyApplicationInput>;
